<template>
	<div>
		<v-card color="transparent" elevation="0">
			<v-card v-if="screen === 'signup'" max-width="750" class="mx-auto" :loading="loaders.submitting || loaders.getFutureDistributions">
				<v-alert v-if="!shineHasScheduledDistribution && futureDistributionsLoaded" type="error" elevation="4">
					<div v-if="summerIsOver"><strong>The summer is over!</strong></div>
					There are no upcoming food distributions at this time.  Please check back with us later or email us at <a href="mailto:information@summerharvest.us" style="color: white;">information@summerharvest.us</a>.
				</v-alert>
				<v-form ref="form" v-model="valid" lazy-validation>
					<v-card-title>Sign up!</v-card-title>
					<v-card-subtitle>
						<span>Sign up for our {{season}} food distributions.  </span>
						<span v-if="season === 'summer'">We hand out food every 2 weeks during the summer.</span>
					</v-card-subtitle>
					<v-divider />
					<v-stepper v-model="step" elevation="0">
						<v-stepper-header>
							<v-stepper-step
								:complete="step > 1"
								:step="1"
							>
								About you
							</v-stepper-step>
							<v-divider />
							<v-stepper-step
								:complete="step > 2"
								:step="2"
							>
								Your kids
							</v-stepper-step>
							<v-divider />
							<v-stepper-step
								:complete="step > 3"
								:step="3"
							>
								Confirm
							</v-stepper-step>
						</v-stepper-header>
						<v-stepper-items>
							<v-stepper-content :step="1">
								<v-text-field
									label="Your first name"
									v-model="model.firstName"
									hint="Your first name as a parent/guardian - not your child's name."
									:autofocus="step === 1"
									:rules="firstNameRules"
								/>
								<v-text-field
									label="Your last name"
									v-model="model.lastName"
									hint="Your last name as a parent/guardian - not your child's name."
									:rules="lastNameRules"
								/>
								<v-text-field
									label="Your email address"
									v-model="model.emailAddress"
									hint="Email address where you would like to receive reminders"
									:rules="emailAddressRules"
								/>
								<v-text-field
									label="Your phone number"
									v-model="model.phoneNumber"
									hint="Phone number"
									:rules="phoneNumberRules"
									@change="modelFormatter"
								/>
								<v-select
									label="Pick up location"
									v-model="model.location"
									:items="availableLocations"
									item-key="locationName"
									item-text="locationName"
									return-object
									:rules="objectChosenRules"
									:loading="loaders.getLocations"
								/>
								<!--
								<v-container>
									<v-row>
										<v-col 
											:cols="12" 
											lg="4"
											md="4"
											sm="4"
											xs="4"
											class="pa-0 pr-2"
										>
											<v-select
												label="Your birth month"
												v-model="model.birthMonth"
												hint="The month you were born"
												:rules="birthMonthRules"
												:items="['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']"
											/>
										</v-col>
										<v-col 
											:cols="12" 
											lg="4"
											md="4"
											sm="4"
											xs="4"
											class="pa-0 pr-2"
										>
											<v-select
												label="Your birth day"
												v-model="model.birthDay"
												hint="The day you were born"
												:rules="birthDayRules"
												:items="daysOfTheMonth"
											/>
										</v-col>
										<v-col 
											:cols="12" 
											lg="4"
											md="4"
											sm="4"
											xs="4"
											class="pa-0"
										>
											<v-select
												label="Your birth year"
												v-model="model.birthYear"
												hint="The year you were born"
												:rules="birthYearRules"
												:items="possibleBirthYears"
											/>
										</v-col>
									</v-row>
								</v-container>
								-->
								<v-switch
									v-model="model.senior"
									label="There is a senior citizen in my household"
									class="pl-2"
								/>
								<v-switch
									v-model="model.veteran"
									label="There is a veteran in my household"
									class="pl-2"
								/>
								<v-switch
									v-model="model.glutenFree"
									label="My family requires a gluten-free diet"
									class="pl-2"
								/>
								<v-switch
									v-model="model.vegetarian"
									label="My family requires a vegetarian diet"
									class="pl-2"
								/>
								<v-text-field
									label="Number of adults in the household"
									v-model="model.numberOfAdults"
									hint="The number of adults, including you, in your household."
									:rules="numberOfAdultsRules"
									type="number"
								/>
								<v-combobox
									label="Language spoken at home"
									v-model="model.language"
									hint="The languange most commonly spoken in hour household"
									:items="langs"
								/>
								<v-text-field
									label="Street Address"
									v-model="model.streetAddress"
									hint="Example: 1234 Main St"
									:rules="streetAddressRules"
								/>
								<v-text-field
									label="Unit Number"
									v-model="model.unitNumber"
									hint="Example: Apt 23"
								/>
								<v-text-field
									label="City"
									v-model="model.city"
									hint="Example: Los Alamitos"
									:rules="cityRules"
								/>
								<v-text-field
									label="State"
									v-model="model.state"
									hint="Example: CA"
									:rules="stateRules"
								/>
								<v-text-field
									label="Zip Code"
									v-model="model.zip"
									hint="Example: 90720"
									:rules="zipRules"
								/>
							</v-stepper-content>
							<v-stepper-content :step="2">
								<v-card
									v-for="(child, index) in model.children"
									:key="child.key"
									class="mb-4 pa-4"
									:color="childCardColor(child.childGender)"
								>
									<v-text-field
										:label="`Child ${index + 1}: First name`"
										v-model="child.firstName"
										:hint="`The first name of child #${index + 1}`"
										:rules="firstNameRules"
									/>
									<v-text-field
										:label="`Child ${index + 1}: Last name`"
										v-model="child.lastName"
										:hint="`The first name of child #${index + 1}`"
										:rules="lastNameRules"
									/>
									<v-container>
										<v-row>
											<v-col 
												:cols="12" 
												lg="4"
												md="4"
												sm="4"
												xs="4"
												class="pa-0 pr-2"
											>
												<v-select
													label="Birth month"
													v-model="child.birthMonth"
													hint="The month this child was born"
													:rules="birthMonthRules"
													:items="['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']"
												/>
											</v-col>
											<v-col 
												:cols="12" 
												lg="4"
												md="4"
												sm="4"
												xs="4"
												class="pa-0 pr-2"
											>
												<v-select
													label="Birth day"
													v-model="child.birthDay"
													hint="The day this child was born"
													:rules="birthDayRules"
													:items="daysOfTheMonth"
												/>
											</v-col>
											<v-col 
												:cols="12" 
												lg="4"
												md="4"
												sm="4"
												xs="4"
												class="pa-0"
											>
												<v-select
													label="Birth year"
													v-model="child.birthYear"
													hint="The year this child was born"
													:rules="birthYearRules"
													:items="possibleBirthYears"
												/>
											</v-col>
										</v-row>
									</v-container>
									<v-autocomplete
										label="School"
										v-model="child.school"
										:items="availableSchools"
										item-key="schoolName"
										item-text="schoolName"
										return-object
										:rules="childSchoolChosenRules"
										:loading="loaders.getSchools"
									/>
									<v-select
										v-model="child.childGender"
										label="Gender"
										:items="['Male', 'Female', 'Not Specified']"
									/>
									<div class="mb-4">
										<v-btn color="primary" small @click="removeChild(index)" :disabled="index === 0">Remove Child</v-btn>
									</div>
								</v-card>
								<div class="mb-4">
									<v-btn color="primary" small @click="addChild">Add Child</v-btn>
								</div>
							</v-stepper-content>
							<v-stepper-content :step="3">
								<v-alert type="info" outlined>
									Please be sure that the following is correct.
								</v-alert>
								<div class="mb-2">
									<div class="overline">Your name:</div>
									<div>{{model.firstName}} {{model.lastName}}</div>
								</div>
								<div class="mb-2">
									<div class="overline">Your email address:</div>
									<div>{{model.emailAddress}}</div>
								</div>
								<div class="mb-2">
									<div class="overline">Your phone number:</div>
									<div>{{formatPhoneNumber(model.phoneNumber)}}</div>
								</div>
								<div class="mb-2" v-if="model.location">
									<div class="overline">Your preferred location:</div>
									<div>{{model.location.locationName}}</div>
								</div>
								<div class="mb-2">
									<div class="overline">Senior in household?</div>
									<div>
										<span v-if="model.senior"><v-icon color="green">mdi-check-circle-outline</v-icon> Yes</span>
										<span v-else><v-icon color="red">mdi-close-octagon-outline</v-icon> No</span>
									</div>
								</div>
								<div class="mb-2">
									<div class="overline">Veteran in household?</div>
									<div>
										<span v-if="model.veteran"><v-icon color="green">mdi-check-circle-outline</v-icon> Yes</span>
										<span v-else><v-icon color="red">mdi-close-octagon-outline</v-icon> No</span>
									</div>
								</div>
								<div class="mb-2">
									<div class="overline">Require a gluten-free diet?</div>
									<div>
										<span v-if="model.glutenFree"><v-icon color="green">mdi-check-circle-outline</v-icon> Yes</span>
										<span v-else><v-icon color="red">mdi-close-octagon-outline</v-icon> No</span>
									</div>
								</div>
								<div class="mb-2">
									<div class="overline">Require a vegetarian diet?</div>
									<div>
										<span v-if="model.vegetarian"><v-icon color="green">mdi-check-circle-outline</v-icon> Yes</span>
										<span v-else><v-icon color="red">mdi-close-octagon-outline</v-icon> No</span>
									</div>
								</div>
								<div class="mb-2">
									<div class="overline">Number of adults in the household:</div>
									<div>{{model.numberOfAdults}}</div>
								</div>
								<div class="mb-2">
									<div class="overline">Language spoken at home:</div>
									<div>{{model.language}}</div>
								</div>
								<div class="mb-2">
									<div class="overline">Your address:</div>
									<div>{{model.streetAddress}} {{model.unitNumber}}</div>
									<div>{{model.city}} {{model.state}} {{model.zip}}</div>
								</div>
								<div class="mb-2">
									<div class="overline">Children:</div>
									<div v-for="(child, childIndex) in model.children" :key="child.key + '-review'" class="mb-2">
										<v-chip small class="float-left elevation-2 ml-1">{{childIndex + 1}}</v-chip>
										<div class="ml-10">{{child.firstName}} {{child.lastName}}</div>
										<div class="ml-10">Born {{moment(child.childBirthday).format("MMMM Do YYYY")}}</div>
										<div class="ml-10" v-if="child.school">{{child.school.schoolName}}</div>
										<div class="ml-10 caption">{{child.childGender}}</div>
									</div>
								</div>
							</v-stepper-content>
						</v-stepper-items>
					</v-stepper>
					<v-divider />
					<v-card-actions>
						<v-spacer />
						<v-btn text v-if="step > 1" @click="goBack">Back</v-btn>
						<v-btn type="submit" color="primary" :disabled="!valid" @click="submit" :loading="loaders.submitting">{{buttonText}}</v-btn>
					</v-card-actions>
				</v-form>
			</v-card>
			<v-card v-if="screen === 'done'" max-width="750" class="mx-auto">
				<v-card-text>
					<v-alert type="success">
						<div>
							<strong>Great!</strong> You have signed up!
						</div>
						<v-divider />
						<div>
							Thanks for signing up.  Look for an email from our team about the next distribution date and time.
						</div>
					</v-alert>
				</v-card-text>
			</v-card>
		</v-card>
		<v-snackbar
			v-model="errorSnackbar"
			:timeout="timeout"
			:top="true"
			color="red accent-2"
		>
			{{errorSnackbarText}}
		</v-snackbar>
		<div>
			<v-btn text x-small @click="showDebugData = !showDebugData">.</v-btn>
			<v-btn text x-small @click="clearLog">.</v-btn>
			<div v-if="showDebugData">
				<pre><code>
{{model}}
</code></pre>
				<pre><code>
{{modelFormatterLog}}
</code></pre>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import FieldDate from '@/components/forms/FieldDate';
import tools from '@/tools';
import apiService from '@/apiService';
export default {
	components: {
		FieldDate
	},
	data() {
		return {
			showDebugData: false,
			errorSnackbar: false,
			errorSnackbarText: '',
			timeout: 4000,
			screen: 'signup',
			moment,
			loaders: {
				submitting: false,
				getLocations: false,
				getSchools: false,
				getFutureDistributions: false
			},
			step: 1,
			valid: false,
			availableLocations: [],
			futureDistributions: [],
			futureDistributionsLoaded: false,
			availableSchools: [],
			response: {},
			modelFormatterLog: [],
			model: {
				emailAddress: '',
				phoneNumber: '',
				firstName: '',
				lastName: '',
				location: null,
				birthMonth: '',
				birthDay: '',
				birthYear: '',
				senior: false,
				veteran: false,
				glutenFree: false,
				vegetarian: false,
				numberOfAdults: 1,
				language: 'English',
				streetAddress: '',
				unitNumber: '',
				city: '',
				state: 'CA',
				zip: '',
				children: []
			},
			firstNameRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 1 ? 'First name must be at least 1 character' : true
			}],
			lastNameRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 1 ? 'Last name must be at least 1 character' : true
			}],
			birthMonthRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 1 ? 'Birth month is required' : true
			}],
			birthDayRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 1 ? 'Birth day is required' : true
			}],
			birthYearRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 1 ? 'Birth year is required' : true
			}],
			numberOfAdultsRules: [v => {
				let num = +v;
				return num < 1 ? 'There needs to be at least one adult in the household' : true
			}],
			streetAddressRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 4 ? 'Street address needs to be at least 4 characters' : true
			}],
			cityRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 2 ? 'City is required' : true
			}],
			stateRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length < 1 ? 'State is required' : true
			}],
			zipRules: [v => {
				let str = v || '';
				str = str.toString();
				return str.length !== 5 ? 'Zip code should be 5 characters' : true
			}],
			objectChosenRules: [v => {
				if (v === null) return 'Please choose your preferred location';
				if (typeof v !== 'object') return 'Please choose your preferred location';
				return true;
			}],
			childSchoolChosenRules: [v => {
				console.log('childSchoolChosenRules', v, typeof v);
				if (v === null) return 'Please choose a school for this child';
				if (typeof v !== 'object') return 'Please choose a school for this child';
				if (typeof v === 'object' && !v.schoolCode) return 'Please choose a school for this child';
				return true;
			}],
			emailAddressRules: [
				v => !!v || 'E-mail is required',
				v => /.+@.+/.test(v) || 'E-mail must be valid'
			],
			phoneNumberRules: [v => {
				let str = v || '';
				str = str.toString();
				let arrNums = str.match(/\d/g);
				if (!arrNums) arrNums = [];
				return arrNums.join('') !== str || arrNums.length !== 10 ? 'Please provide a 10-digit phone number only' : true
			}]
		}
	},
	computed: {
		season() {
			if ([1, 2, 3, 4, 5, 6, 7, 8].includes(moment().month())) return 'summer';
			return 'winter';
		},
		buttonText() {
			if (this.step === 1) return 'Next';
			if (this.step === 2) return 'Next';
			return 'Sign Up!';
		},
		daysOfTheMonth() {
			if (['02'].includes(this.model.birthMonth)) {
				return this.getArrayRange(28)
			} else if (['04', '06', '09', '11'].includes(this.model.birthMonth)) {
				return this.getArrayRange(30)
			}
			return this.getArrayRange(31)
		},
		possibleBirthYears() {
			const years = 20;
			const output = [];
			let starter = moment().year() - years;
			Array.from(Array(years).keys()).forEach(x => {
				output.push(starter + '');
				starter++;
			});
			return output;
		},
		langs() {
			return ['English', 'Spanish', 'Filipino', 'Korean', 'Vietnamese', 'Japanese', 'Mandarin', 'Cantonese', 'Hunanese', 'Other'];
		},
		formattedData() {
			const obj = JSON.parse(JSON.stringify(this.model));
			obj.tags = [];
			if (obj.senior) obj.tags.push('Senior');
			if (obj.veteran) obj.tags.push('Veteran');
			if (obj.glutenFree) obj.tags.push('Gluten-Free');
			if (obj.vegetarian) obj.tags.push('Vegetarian');
			return obj;
		},
		shineHasScheduledDistribution() {
			return this.futureDistributions.length > 0;
		},
		summerIsOver() {
			if ([7, 8, 9, 10, 11].includes(moment().month())) return true;
			return false;
		}
	},
	methods: {
		showError(options) {
			this.errorSnackbar = options.show;
			this.errorSnackbarText = options.text;
		},
		childCardColor(gender) {
			if (gender === 'Male') return 'light-blue lighten-5';
			if (gender === 'Female') return 'pink lighten-5';
			return 'yellow lighten-5';
		},
		formatPhoneNumber(phoneNumberString) {
			const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
			const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
			if (match && match.length >= 4) {
				return '(' + match[1] + ') ' + match[2] + '-' + match[3]
			}
			return phoneNumberString;
		},
		clearLog() {
			this.modelFormatterLog = [];
		},
		modelFormatter() {
			this.modelFormatterLog.push('init ' + this.step);
			try {
				if (this.model.phoneNumber && this.model.phoneNumber.length === 11 && this.model.phoneNumber[0] === '1') {
					this.model.phoneNumber = this.model.phoneNumber.slice(1);
				}
				this.model.children.forEach(child => {
					let str = '';
					if (child.birthMonth && child.birthDay && child.birthYear) {
						str = `${child.birthYear}-${child.birthMonth}-${child.birthDay}T12:00:00`;
					}
					console.log('Converting child birth data to a string', child, { str });
					this.modelFormatterLog.push(str + ' ' + this.step);
					this.modelFormatterLog.push(child.birthMonth + ' ' + child.birthDay + ' ' + child.birthYear + ' ' + this.step);
					if (str) {
						child.childBirthday = moment(str).toDate();
					}
				});
			} catch(err) {
				this.modelFormatterLog.push('err ' + err);
			}
			this.modelFormatterLog.push('finish ' + this.step);
		},
		removeChild(index) {
			this.$delete(this.model.children, index);
		},
		addChild() {
			this.model.children.push(this.defaultChildObject())
		},
		defaultChildObject() {
			return {
				key: tools.randomString(),
				firstName: '',
				lastName: '',
				school: {
					schoolCode: '',
					schoolName: ''
				},
				birthMonth: '',
				birthDay: '',
				birthYear: '',
				childBirthday: null,
				childGender: 'Male'
			}
		},
		getArrayRange(num) {
			const filled = Array.from(Array(num).keys());
			const output = [];
			let numberer = 0;
			filled.forEach(x => {
				numberer++;
				let str = numberer + '';
				if (str.length === 1) str = '0' + str;
				output.push(str);
			});
			console.log('getArrayRange', num, output.length);
			return output;
		},
		goBack() {
			this.step = this.step - 1;
			this.valid = true;
			if ([1].includes(this.step)) {
				this.model.children.splice(0); 
				// Empty the children array in a reactive way, sadly we must do this because of the lazy validation.
				// If the children fields are not valid, and you're viewing step 1 in the stepper, you cannot 
				// proceed back to step 2 because of the validation errors on fields you cannot even see.
			}
		},
		submit(e) {
			this.modelFormatter();
			if (e) e.preventDefault();
			console.log('submit', this.step);
			if (this.$refs.form.validate()) {
				console.log('validate', this.step);
				if (!this.shineHasScheduledDistribution) {
					this.showError({
						show: true,
						text: 'There are no upcoming distributions that are announced at this time. Check back later when we have announced them.'
					});
					return;
				}
				if ([1].includes(this.step)) {
					this.step++;
					if (this.model.children.length === 0) {
						this.model.children.push(this.defaultChildObject());
					}
					return;
				}
				if ([2].includes(this.step)) {
					this.step++;
					return;
				}
				if ([3].includes(this.step)) {
					this.submitSignUp();
					return;
				}
			} else {
				console.log('no validate', this.step, `kids: ${this.model.children.length}`);
				if ([1].includes(this.step) && this.model.children.length === 1 && this.model.children.filter(c => c.firstName === '').length === 0) {
					// they have some children data. Let them go to the next step
					this.step++;
				}
			}
		},
		getLocations() {
			this.loaders.getLocations = true;
			apiService.action({
				namespace: 'people',
				pluralName: 'clients',
				action: 'getAvailableLocations',
				method: 'get'
			}, (err, response) => {
				this.loaders.getLocations = false;
				if (err) {
					this.showError({
						show: true,
						text: err.message
					});
					return;
				}
				this.availableLocations = response;
			});
		},
		getFutureDistributions() {
			this.loaders.getFutureDistributions = true;
			apiService.action({
				namespace: 'people',
				pluralName: 'clients',
				action: 'getFutureDistributions',
				method: 'get'
			}, (err, response) => {
				this.loaders.getFutureDistributions = false;
				if (err) {
					this.showError({
						show: true,
						text: err.message
					});
					return;
				}
				this.futureDistributions = response;
				this.futureDistributionsLoaded = true;
			});
		},
		getSchools() {
			this.loaders.getSchools = true;
			apiService.action({
				namespace: 'people',
				pluralName: 'clients',
				action: 'getAvailableSchools',
				method: 'get'
			}, (err, response) => {
				this.loaders.getSchools = false;
				if (err) {
					this.showError({
						show: true,
						text: err.message
					});
					return;
				}
				this.availableSchools = response;
			});
		},
		submitSignUp() {
			this.loaders.submitting = true;
			apiService.action({
				namespace: 'people',
				pluralName: 'clients',
				action: 'registerClient',
				method: 'post',
				payload: {
					client: this.formattedData
				}
			}, (err, response) => {
				this.loaders.submitting = false;
				if (err) {
					this.showError({
						show: true,
						text: err.message
					});
					return;
				}
				this.screen = 'done';
				this.response = response;
			});
		}
	},
	created() {
		this.getLocations();
		this.getSchools();
		this.getFutureDistributions();
	}
}
</script>